<script lang="ts">
  import AdminNav from '@/components/admin/AdminNav.svelte'
  import { router } from '@inertiajs/svelte'

  interface User {
    id: string
    name: string
    email: string
    warning_count?: number
  }

  interface Board {
    name: string
  }

  interface Post {
    id: string
    title: string
    content: string
    created_at: string
    board?: Board
    user?: User
  }

  interface Reporter {
    name: string
  }

  interface Report {
    id: string
    reason: string
    reason_label?: string
    description?: string
    urgency_level: string
    created_at: string
    reporter?: Reporter
  }

  interface ReviewAdmin {
    name: string
  }

  interface Review {
    id: string
    status: string
    report_count_at_creation: number
    admin_note?: string
    decided_at?: string
    admin?: ReviewAdmin
  }

  interface ReportStats {
    total: number
    by_reason?: Record<string, number>
    latest_at?: string
  }

  let {
    review = {} as Review,
    post = {} as Post,
    reports = [] as Report[],
    report_stats = {} as ReportStats,
    flash = {} as Record<string, string>
  } = $props()

  let showDecideModal = $state(false)
  let decision = $state<'keep' | 'delete_archive' | ''>('')
  let adminNote = $state('')
  let addWarning = $state(false)
  let warningType = $state('warning')
  let warningContent = $state('')
  let isSubmitting = $state(false)

  function openDecide(d: 'keep' | 'delete_archive') {
    decision = d
    adminNote = ''
    addWarning = false
    warningContent = ''
    showDecideModal = true
  }

  function submitDecision() {
    if (!decision) return
    isSubmitting = true

    router.patch(`/admin/content_reviews/${review.id}/decide`, {
      decision,
      note: adminNote,
      add_warning: addWarning,
      warning_type: warningType,
      warning_content: warningContent
    }, {
      onFinish: () => { isSubmitting = false },
      onSuccess: () => { showDecideModal = false }
    })
  }

  function getUrgencyInfo(level: string) {
    switch (level) {
      case 'critical': return { badge: 'badge-error',   label: '긴급' }
      case 'high':     return { badge: 'badge-warning', label: '높음' }
      default:         return { badge: 'badge-neutral', label: '보통' }
    }
  }

  function getStatusInfo(status: string) {
    switch (status) {
      case 'pending':          return { badge: 'badge-warning', label: '심사 대기' }
      case 'kept':             return { badge: 'badge-success', label: '게시 유지' }
      case 'deleted_archived': return { badge: 'badge-error',   label: '삭제 보관' }
      default:                 return { badge: 'badge-neutral',  label: status }
    }
  }

  const REASON_LABELS: Record<string, string> = {
    spam_commercial:    '반복 홍보, 상업적 게시물',
    personal_info:      '개인정보 노출',
    illegal_content:    '불법 콘텐츠',
    csam:               '아동·청소년 성착취물',
    adult_content:      '성인물, 선정적 콘텐츠',
    hate_harassment:    '차별/모욕/괴롭힘',
    violence_self_harm: '폭력 위협/자해/자살',
    false_info:         '허위 정보',
    other:              '기타'
  }

  let statusInfo = $derived(getStatusInfo(review.status))
</script>

<AdminNav activeTab="content_reviews" />

  <!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <a href="/admin/content_reviews" class="hover:text-primary transition-colors">콘텐츠 심사</a>
    <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
    </svg>
    <span class="text-content">심사 상세</span>
  </div>

  {#if flash.notice}
    <div class="mb-6 p-4 bg-success/10 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
      {flash.notice}
    </div>
  {/if}
  {#if flash.alert}
    <div class="mb-6 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-body2">
      {flash.alert}
    </div>
  {/if}

  <div class="grid lg:grid-cols-3 gap-6 max-w-7xl">

    <!-- 왼쪽: 게시물 본문 -->
    <div class="lg:col-span-2 space-y-6">

      <!-- 상태 + 액션 -->
      <div class="card card-bordered p-6 shadow-sm">
        <div class="flex items-start justify-between mb-4 gap-4">
          <div class="min-w-0">
            <span class={`badge ${statusInfo.badge} mb-2`}>{statusInfo.label}</span>
            <h1 class="text-h1 text-content mt-1">{post.title}</h1>
            <div class="flex items-center gap-3 mt-2 text-caption text-content-tertiary flex-wrap">
              <span>{post.board?.name || ''}</span>
              <span>·</span>
              <span>{post.created_at ? new Date(post.created_at).toLocaleString('ko-KR') : ''}</span>
              <span>·</span>
              <span class="text-error font-medium">신고 {report_stats.total || 0}건</span>
            </div>
          </div>

          {#if review.status === 'pending'}
            <div class="flex items-center gap-2 shrink-0">
              <button
                onclick={() => openDecide('keep')}
                class="btn btn-secondary btn-sm"
              >
                게시 유지
              </button>
              <button
                onclick={() => openDecide('delete_archive')}
                class="btn btn-danger btn-sm"
              >
                삭제 후 보관
              </button>
            </div>
          {/if}
        </div>

        <!-- 본문 -->
        <div class="p-4 bg-surface-tertiary rounded-xl border border-default max-h-96 overflow-y-auto">
          <div class="prose prose-sm max-w-none dark:prose-invert text-body2 text-gray-700 dark:text-dark-text-medium">
            {@html post.content}
          </div>
        </div>
      </div>

      <!-- 처리 완료 결과 -->
      {#if review.status !== 'pending'}
        <div class="card card-bordered p-6 shadow-sm {review.status === 'kept' ? 'border-success/30' : 'border-error/30'}">
          <h2 class="text-title text-content mb-4">처리 결과</h2>
          <dl class="space-y-3 text-body2">
            <div class="flex gap-4">
              <dt class="text-content-tertiary w-20 shrink-0">결정</dt>
              <dd><span class={`badge ${statusInfo.badge}`}>{statusInfo.label}</span></dd>
            </div>
            {#if review.admin}
              <div class="flex gap-4">
                <dt class="text-content-tertiary w-20 shrink-0">처리자</dt>
                <dd class="text-content">{review.admin.name}</dd>
              </div>
            {/if}
            {#if review.decided_at}
              <div class="flex gap-4">
                <dt class="text-content-tertiary w-20 shrink-0">처리일시</dt>
                <dd class="text-content">{new Date(review.decided_at).toLocaleString('ko-KR')}</dd>
              </div>
            {/if}
            {#if review.admin_note}
              <div class="flex gap-4">
                <dt class="text-content-tertiary w-20 shrink-0">메모</dt>
                <dd class="text-content">{review.admin_note}</dd>
              </div>
            {/if}
          </dl>
        </div>
      {/if}

      <!-- 신고 목록 -->
      <div class="card card-bordered overflow-hidden shadow-sm">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">신고 내역 ({report_stats.total || 0}건)</h2>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-dark-border max-h-80 overflow-y-auto">
          {#each reports as r}
            {@const urgInfo = getUrgencyInfo(r.urgency_level)}
            <div class="px-6 py-4">
              <div class="flex items-center gap-2 mb-1.5">
                <span class={`badge ${urgInfo.badge} text-caption`}>{urgInfo.label}</span>
                <span class="text-body2 font-medium text-content">
                  {r.reason_label || REASON_LABELS[r.reason] || r.reason}
                </span>
              </div>
              {#if r.description}
                <p class="text-caption text-content-secondary mb-1">{r.description}</p>
              {/if}
              <p class="text-caption text-gray-400 dark:text-dark-text-disabled">
                {r.reporter?.name || '익명'} · {new Date(r.created_at).toLocaleDateString('ko-KR')}
              </p>
            </div>
          {/each}

          {#if reports.length === 0}
            <div class="px-6 py-8 text-center text-body2 text-gray-400 dark:text-dark-text-disabled">
              신고 내역이 없습니다.
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- 오른쪽: 사이드바 -->
    <div class="space-y-6">

      <!-- 신고 통계 -->
      <div class="card card-bordered p-5 shadow-sm">
        <h2 class="text-title text-content mb-4">신고 사유 분석</h2>
        <div class="space-y-2">
          {#each Object.entries(report_stats.by_reason || {}) as [reason, count]}
            <div class="flex items-center justify-between gap-2">
              <span class="text-body2 text-content-secondary truncate">
                {REASON_LABELS[reason] || reason}
              </span>
              <span class="text-body2 font-bold text-content shrink-0">{count}건</span>
            </div>
          {/each}
          {#if Object.keys(report_stats.by_reason || {}).length === 0}
            <p class="text-caption text-gray-400 dark:text-dark-text-disabled">데이터 없음</p>
          {/if}
        </div>
        {#if report_stats.latest_at}
          <p class="mt-4 pt-4 border-t border-gray-100 dark:border-dark-border text-caption text-gray-400 dark:text-dark-text-disabled">
            최근 신고: {new Date(report_stats.latest_at).toLocaleString('ko-KR')}
          </p>
        {/if}
      </div>

      <!-- 작성자 정보 -->
      <div class="card card-bordered p-5 shadow-sm">
        <h2 class="text-title text-content mb-4">작성자 정보</h2>
        <dl class="space-y-3 text-body2">
          <div class="flex justify-between gap-2">
            <dt class="text-content-tertiary shrink-0">이름</dt>
            <dd class="text-content font-medium text-right">{post.user?.name || '-'}</dd>
          </div>
          <div class="flex justify-between gap-2">
            <dt class="text-content-tertiary shrink-0">이메일</dt>
            <dd class="text-caption text-content-secondary text-right break-all">{post.user?.email || '-'}</dd>
          </div>
          <div class="flex justify-between gap-2">
            <dt class="text-content-tertiary shrink-0">경고 누적</dt>
            <dd class="font-bold {(post.user?.warning_count || 0) > 0 ? 'text-error' : 'text-content'}">
              {post.user?.warning_count || 0}회
            </dd>
          </div>
        </dl>
        {#if post.user?.id}
          <a
            href={`/admin/users/${post.user.id}`}
            class="mt-4 pt-4 border-t border-gray-100 dark:border-dark-border block text-center text-body2 text-primary hover:text-primary-dark font-medium transition-colors"
          >
            회원 상세 보기 →
          </a>
        {/if}
      </div>

    </div>
  </div>

  <!-- 처리 결정 모달 -->
  {#if showDecideModal}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onclick={(e) => { if (e.target === e.currentTarget) showDecideModal = false }}
      onkeydown={(e) => { if (e.key === 'Escape') showDecideModal = false }}
    >
      <div class="w-full max-w-lg bg-surface rounded-2xl shadow-2xl overflow-hidden">

        <div class="px-6 py-4 border-b border-default">
          <h3 class="text-title text-content">
            {decision === 'keep' ? '게시 유지' : '삭제 후 보관'}
          </h3>
          <p class="text-body2 text-content-tertiary mt-1">
            {decision === 'keep'
              ? '신고를 무효화하고 해당 게시물을 공개 상태로 복원합니다.'
              : '게시물을 숨김 처리하고 아카이브에 보관합니다. 작성자가 더 이상 조회할 수 없습니다.'}
          </p>
        </div>

        <div class="px-6 py-5 space-y-4">
          <!-- 처리 사유 -->
          <div>
            <label for="admin-note" class="block text-label font-medium text-gray-700 dark:text-dark-text-medium mb-2">
              처리 사유 (관리자 메모)
            </label>
            <textarea
              id="admin-note"
              bind:value={adminNote}
              rows="3"
              placeholder="처리 근거를 간략히 남겨주세요."
              class="w-full px-3 py-2.5 text-body2 border border-gray-300 dark:border-dark-border
                     bg-surface text-content
                     rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
            ></textarea>
          </div>

          <!-- 작성자 경고/메모 기록 -->
          <div class="border border-default rounded-xl p-4">
            <label class="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" bind:checked={addWarning} class="rounded accent-primary" />
              <span class="text-body2 font-medium text-gray-700 dark:text-dark-text-medium">
                작성자에게 경고/메모 기록하기
              </span>
            </label>

            {#if addWarning}
              <div class="space-y-3 pl-6">
                <div class="flex items-center gap-4">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" bind:group={warningType} value="warning" class="accent-primary" />
                    <span class="text-body2 text-error font-medium">경고</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" bind:group={warningType} value="memo" class="accent-primary" />
                    <span class="text-body2 text-gray-700 dark:text-dark-text-medium">관리 메모</span>
                  </label>
                </div>
                <textarea
                  bind:value={warningContent}
                  rows="2"
                  placeholder={warningType === 'warning' ? '경고 사유를 입력하세요.' : '내부 메모를 입력하세요.'}
                  class="w-full px-3 py-2 text-body2 border border-gray-300 dark:border-dark-border
                         bg-surface text-content
                         rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                ></textarea>
              </div>
            {/if}
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-default bg-surface-tertiary">
          <button
            onclick={() => showDecideModal = false}
            class="btn btn-secondary btn-sm"
            disabled={isSubmitting}
          >
            취소
          </button>
          <button
            onclick={submitDecision}
            disabled={isSubmitting}
            class={`btn btn-sm ${decision === 'keep' ? 'btn-primary' : 'btn-danger'} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {#if isSubmitting}
              <svg class="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              처리 중...
            {:else}
              {decision === 'keep' ? '게시 유지 확정' : '삭제 처리 확정'}
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/if}
